import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  Skeleton,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Filter from "../../Components/Filter/Filter";
import { postData, getData } from "../../Webservice/Webservice";
import NotFound from "../NotFound/NotFound";

const rubikfont = {
  fontFamily: "Rubik, sans-serif",
};



function ProductList() {
  const navigate = useNavigate();
  const location = useLocation();
  const { categoryId, subcategoryId, searchTerm, layoutId, title } = location.state || {};

  const [filterOpen, setFilterOpen] = useState(false);
  const [priceRange, setPriceRange] = useState([0, 5000]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedIdealFor, setSelectedIdealFor] = useState("all");
  const [selectedColors, setSelectedColors] = useState([]);
  const [selectedContainerType, setSelectedContainerType] = useState([]);
  const [selectedUnitType, setSelectedUnitType] = useState([]);
  const [codAvailable, setCodAvailable] = useState("all");

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchProducts = async () => {
      if (categoryId || subcategoryId || searchTerm || layoutId) {
        setLoading(true);
        try {
          const tokenValue = sessionStorage.getItem("token");
          const token = tokenValue ? `Bearer ${tokenValue}` : "";

          let response;
          if (searchTerm) {
            response = await getData('product/search', { search: searchTerm }, token, false);
          } else if (layoutId) {
            response = await getData(`homepage/layout/${layoutId}`, {}, token);
          } else {
            const params = new URLSearchParams();
            if (categoryId) params.append("category_id", categoryId);
            if (subcategoryId) params.append("subcategory_id", subcategoryId);

            response = await postData(
              `product/list?${params.toString()}`,
              {},
              token
            );
          }

          let productList = [];

          if (response.results) {
            productList = response.results;
          } else if (response.products) {
            productList = response.products;
          } else if (response.layout && response.layout.product_id) {
            productList = response.layout.product_id;
          } else if (response.product_id) {
            productList = response.product_id;
          }

          if (productList && Array.isArray(productList)) {
            const mappedProducts = productList.map((p) => ({
              img:
                p.product_images && p.product_images.length > 0
                  ? p.product_images[0]
                  : "https://via.placeholder.com/300",
              category: p.category_id?.title || "",
              name: p.title,
              price: `₹${p.b2b_sellingPrice || 0}`,
              brand: p.brand || "Generic",
              idealFor: p.ideal_for || "unisex",
              color: p.color || "",
              id: p._id,
              mrp: p.b2c_mrp || 0,
              b2b_discountValue: p.b2b_discountValue,
              b2b_discountType: p.b2b_discountType,
            }));
            setProducts(mappedProducts);
          } else {
            setProducts([]);
          }
        } catch (error) {
          console.error("Error fetching products:", error);
          setProducts([]);
        } finally {
          setLoading(false);
        }
      } else {
        // Fallback if accessed directly or without state
        setProducts([]);
      }
    };

    fetchProducts();
  }, [categoryId, subcategoryId, searchTerm, layoutId]);

  const mapProductData = (productList) => {
    if (productList && Array.isArray(productList)) {
      return productList.map((p) => ({
        img:
          p.product_images && p.product_images.length > 0
            ? p.product_images[0]
            : "https://via.placeholder.com/300",
        category: p.category_id?.title || "",
        name: p.title,
        price: `₹${p.b2b_sellingPrice || 0}`,
        brand: p.brand || "Generic",
        idealFor: p.ideal_for || "unisex",
        color: p.color || "",
        id: p._id,
        mrp: p.b2c_mrp || 0,
        b2b_discountValue: p.b2b_discountValue,
        b2b_discountType: p.b2b_discountType,
      }));
    }
    return [];
  };

  const applyFilters = async () => {
    setLoading(true);
    setFilterOpen(false); // Close drawer on apply
    try {
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";

      const params = new URLSearchParams();

      // Add context parameters if they exist
      if (categoryId) params.append("category_id", categoryId);
      if (subcategoryId) params.append("subcategory_id", subcategoryId);
      if (searchTerm) params.append("search", searchTerm);

      // Add Filter parameters
      if (selectedIdealFor && selectedIdealFor !== "all") {
        params.append("ideal_for", selectedIdealFor);
      }

      if (selectedBrands.length > 0) {
        selectedBrands.forEach(brand => params.append("brand", brand));
      }

      if (selectedContainerType.length > 0) {
        selectedContainerType.forEach(type => params.append("container_type", type));
      }

      if (selectedUnitType.length > 0) {
        selectedUnitType.forEach(type => params.append("unitType", type));
      }

      if (codAvailable && codAvailable !== "all") {
        // Assuming API expects boolean string or specific value. 
        // User example: codAvailable=true. 
        // State is "yes"/"no". 
        params.append("codAvailable", codAvailable === "yes" ? "true" : "false");
      }

      // Price Range
      params.append("min", priceRange[0]);
      params.append("max", priceRange[1]);

      const queryString = params.toString();
      const response = await postData(`product/list?${queryString}`, {}, token);

      let productList = [];
      if (response.results) {
        productList = response.results;
      } else if (response.products) {
        productList = response.products;
      } else if (response.product_id) {
        productList = response.product_id;
      }

      const mappedProducts = mapProductData(productList);
      setProducts(mappedProducts);

    } catch (error) {
      console.error("Error applying filters:", error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const brands = [...new Set(products.map((p) => p.brand))];
  const colors = [...new Set(products.map((p) => p.color))];

  const clearAllFilters = () => {
    setPriceRange([0, 5000]);
    setSelectedBrands([]);
    setSelectedIdealFor("all");
    setSelectedColors([]);
    setSelectedContainerType([]);
    setSelectedUnitType([]);
    setCodAvailable("all");
  };

  // Skeleton component for product cards
  const ProductCardSkeleton = () => (
    <Box
      sx={{
        bgcolor: "white",
        borderRadius: 2,
        overflow: "hidden",
        border: "1px solid #f0f0f0",
      }}
    >
      {/* Product Image Skeleton */}
      <Box
        sx={{
          position: "relative",
          paddingTop: "90%",
          bgcolor: "#f8f8f8",
        }}
      >
        <Skeleton
          variant="rectangular"
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
          }}
        />
        {/* Discount Badge Skeleton */}
        <Skeleton
          variant="rectangular"
          width={60}
          height={24}
          sx={{
            position: "absolute",
            top: 10,
            left: 10,
            borderRadius: 1,
          }}
        />
        {/* Action Icons Skeleton */}
        <Box
          sx={{
            position: "absolute",
            right: 10,
            top: "40%",
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Skeleton variant="circular" width={40} height={40} />
          <Skeleton variant="circular" width={40} height={40} />
          <Skeleton variant="circular" width={40} height={40} />
        </Box>
      </Box>

      {/* Product Details Skeleton */}
      <Box sx={{ p: { xs: 1, sm: 1.5, md: 2 } }}>
        <Skeleton
          variant="text"
          width="90%"
          height={20}
          sx={{ minHeight: { xs: "36px", sm: "44px", md: "48px" } }}
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
          <Skeleton variant="text" width={80} height={24} />
          <Skeleton variant="text" width={60} height={20} sx={{ textDecoration: "line-through" }} />
        </Box>
      </Box>
    </Box>
  );

  if (loading) {
    return (
      <Container sx={{ py: { xs: 5, md: 8 } }}>
        {/* Header Skeleton */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: { xs: 4, md: 6 },
          }}
        >
          <Skeleton variant="text" width="60%" height={43} />
          <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: "4px" }} />
        </Box>

        {/* Product Grid Skeleton */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, 1fr)",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
              lg: "repeat(4, 1fr)",
            },
            gap: { xs: 2, sm: 3, md: 4 },
          }}
        >
          {[...Array(8)].map((_, index) => (
            <ProductCardSkeleton key={index} />
          ))}
        </Box>
      </Container>
    );
  }

  // If accessed directly via URL without appropriate state, show 404
  if (!loading && !categoryId && !subcategoryId && !searchTerm && !layoutId) {
    return <NotFound />;
  }

  return (
    <Container sx={{ py: { xs: 5, md: 8 } }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: { xs: 4, md: 6 },
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontSize: { xs: "28px", sm: "36px", md: "43.75px" },
            color: "#222",
            ...rubikfont,
            letterSpacing: "1.5px",
          }}
        >
          {searchTerm ? `SEARCH RESULTS FOR "${searchTerm.toUpperCase()}"` : (title ? title.toUpperCase() : "PRODUCT LIST")}
        </Typography>

        <Button
          variant="outlined"
          startIcon={<FilterListIcon />}
          onClick={() => setFilterOpen(true)}
          sx={{
            ...rubikfont,
            borderColor: "#222",
            color: "#222",
            "&:hover": {
              borderColor: "#222",
              backgroundColor: "#f5f5f5",
            },
          }}
        >
          Filter
        </Button>
      </Box>

      {/* Filter  */}
      <Filter
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        priceRange={priceRange}
        setPriceRange={setPriceRange}
        selectedBrands={selectedBrands}
        setSelectedBrands={setSelectedBrands}
        selectedIdealFor={selectedIdealFor}
        setSelectedIdealFor={setSelectedIdealFor}
        selectedColors={selectedColors}
        setSelectedColors={setSelectedColors}
        brands={brands}
        colors={colors}
        onClearFilters={clearAllFilters}
        selectedContainerType={selectedContainerType}
        setSelectedContainerType={setSelectedContainerType}
        selectedUnitType={selectedUnitType}
        setSelectedUnitType={setSelectedUnitType}
        codAvailable={codAvailable}
        setCodAvailable={setCodAvailable}
        onApply={applyFilters}
      />

      {/* Product Grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, 1fr)",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
            lg: "repeat(4, 1fr)",
          },
          gap: { xs: 2, sm: 3, md: 4 },
        }}
      >
        {products.length === 0 ? (
          <Box sx={{ gridColumn: "1 / -1", textAlign: "center", }}>
            <img src="/img/nopr1.png" alt="noProduct" />
            <Typography variant="h6" color="text.secondary">
              {searchTerm ? "No products found." : "No products found in this subcategory."}
            </Typography>
          </Box>
        ) : (
          products.map((product, index) => (
            <Box
              onClick={() => navigate(`/product/${product.id}`, { state: { product } })}
              key={index}
              className="product-card"
              sx={{
                bgcolor: "white",
                borderRadius: 2,
                overflow: "hidden",
                transition: "all 0.3s ease",
                cursor: "pointer",
                border: "1px solid #f0f0f0",
                "&:hover": {
                  transform: "translateY(-8px)",
                  boxShadow: "0 8px 20px rgba(0,0,0,0.12)",
                  "& .product-image": {
                    transform: "scale(1.08)",
                  },
                  "& .action-icons": {
                    transform: "translateY(-50%) translateX(0)",
                    opacity: 1,
                  },
                },
              }}
            >
              {/* Product Image */}
              <Box
                sx={{
                  position: "relative",
                  paddingTop: "90%",
                  overflow: "hidden",
                  bgcolor: "#f8f8f8",
                }}
              >
                <img
                  src={product.img}
                  alt={product.name}
                  className="product-image"
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    transition: "transform 0.4s ease",
                  }}
                />
                {/* ✅ B2B Discount Badge – TOP LEFT */}
                {product.b2b_discountValue && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 10,
                      left: 10,
                      bgcolor: "#FF6B35",
                      color: "white",
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: { xs: "11px", sm: "12px", md: "13px" },
                      fontWeight: 600,
                      zIndex: 3,
                      boxShadow: "0 4px 10px rgba(0,0,0,0.25)",
                      ...rubikfont,
                    }}
                  >
                    {product.b2b_discountType === "percentage"
                      ? `${product.b2b_discountValue}% OFF`
                      : `₹${product.b2b_discountValue} OFF`}
                  </Box>
                )}

                <Box
                  className="action-icons"
                  sx={{
                    position: "absolute",
                    right: 10,
                    top: "40%",
                    transform: "translateY(-50%) translateX(60px)",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    opacity: 0,
                    transition: "all 0.4s ease",
                  }}
                >
                  {/* <IconButton
                    sx={{
                      bgcolor: "white",
                      width: { xs: 35, sm: 40, md: 45 },
                      height: { xs: 35, sm: 40, md: 45 },
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      "&:hover": {
                        bgcolor: "#222",
                        color: "white",
                        transform: "scale(1.1)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    <FavoriteBorderIcon sx={{ fontSize: { xs: 16, sm: 18, md: 20 } }} />
                  </IconButton>
                  <IconButton
                    sx={{
                      bgcolor: "white",
                      width: { xs: 35, sm: 40, md: 45 },
                      height: { xs: 35, sm: 40, md: 45 },
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      "&:hover": {
                        bgcolor: "#222",
                        color: "white",
                        transform: "scale(1.1)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    <ShoppingCartOutlinedIcon sx={{ fontSize: { xs: 16, sm: 18, md: 20 } }} />
                  </IconButton> */}
                  {/* <IconButton
                    onClick={() => navigate(`/product/${product.id}`, { state: { product } })}
                    sx={{
                      bgcolor: "white",
                      width: { xs: 35, sm: 40, md: 45 },
                      height: { xs: 35, sm: 40, md: 45 },
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      "&:hover": {
                        bgcolor: "#222",
                        color: "white",
                        transform: "scale(1.1)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    <VisibilityOutlinedIcon sx={{ fontSize: { xs: 16, sm: 18, md: 20 } }} />
                  </IconButton> */}
                </Box>

              </Box>

              {/* Product Details */}
              <Box sx={{ p: { xs: 1, sm: 1.5, md: 2 } }}>
                <Typography
                  sx={{
                    fontSize: { xs: "14px", sm: "16px", md: "18px" },
                    fontWeight: 500,
                    color: "#222",
                    // mb: 1.5,
                    ...rubikfont,
                    minHeight: { xs: "36px", sm: "44px", md: "48px" },
                  }}
                >
                  {product.name}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {/*b2b_sellingPrice */}
                  <Typography
                    sx={{
                      fontSize: { xs: "16px", sm: "18px", md: "20px" },
                      fontWeight: 600,
                      color: "#222",
                      ...rubikfont,
                    }}
                  >
                    {product.price}
                  </Typography>
                  {/*b2c_mrp*/}
                  <Typography
                    sx={{
                      fontSize: { xs: "13px", sm: "14px", md: "16px" },
                      color: "red",
                      textDecoration: "line-through",
                      ...rubikfont,
                    }}
                  >
                    {product.mrp ? `₹${product.mrp}` : ''}
                  </Typography>
                </Box>

              </Box>
            </Box>
          ))
        )}
      </Box>
    </Container>
  );
}

export default ProductList;